// Transform Dawn's Color variant pills into colored circles.
// - Single color  →  solid circle
// - Multi-color   →  conic-gradient pie slices
// - All swatches are uniform size; ring styling is reserved for OOS variants only.
// - Tooltip on hover shows the full option name.
//
// Re-applies automatically when Dawn re-renders the variant picker on option
// change (via MutationObserver on document.body).

(function () {
  const COLOR_MAP = {
    Black: '#1F0E33',
    Nude: '#E5C6A9',
    Gray: '#9A9AA0',
    'Dark Blue': '#2A3A6B',
    'Dark Purple': '#4B2C7A',
    Pink: '#F5B6C7',
    Blue: '#8DC3E1',
    Green: '#B7DAB9',
    Lace: '#1F0E33',
  }

  function parseValue(raw) {
    let text = raw.trim()
    // Strip Hipster Flow prefix but keep the underlying color parts;
    // Heavy vs Light no longer changes swatch shape.
    text = text.replace(/^(Heavy|Light)(?:\s+Flow)?\s*—\s*/, '')
    text = text.replace(/\s+with\s+lace/i, '')
    const parts = text.split(/\s*\/\s*/).map((s) => s.trim()).filter(Boolean)
    return { colors: parts }
  }

  // Must match the slug used in scripts/attach-color-images.mjs alt="bam-color:<slug>".
  function colorSlug(raw) {
    return raw
      .toLowerCase()
      .replace(/\s*—\s*/g, '-')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  }

  function makeSwatch(value, oos) {
    const { colors } = parseValue(value)
    const el = document.createElement('span')
    el.className = 'bam-swatch bam-swatch--' + (oos ? 'ring' : 'full')
    if (colors.length === 1) {
      el.style.background = COLOR_MAP[colors[0]] || '#ccc'
    } else {
      const step = 360 / colors.length
      const stops = colors
        .map((c, i) => `${COLOR_MAP[c] || '#ccc'} ${i * step}deg ${(i + 1) * step}deg`)
        .join(', ')
      el.style.background = `conic-gradient(from -90deg, ${stops})`
    }
    return el
  }

  // Detect out-of-stock/unavailable state on the paired variant input.
  function isLabelOOS(label) {
    if (label.htmlFor) {
      const input = document.getElementById(label.htmlFor)
      if (input && (input.disabled || input.classList.contains('disabled'))) return true
    }
    // Dawn injects a visually-hidden span with "sold out"/"unavailable" text when OOS.
    const hidden = label.querySelector('.visually-hidden')
    if (hidden && /sold out|unavailable/i.test(hidden.textContent)) return true
    return false
  }

  function readOptionName(fieldset) {
    const candidates = [
      'legend', '.form__label', '.variant-picker__option-name',
      '.product-form__label', '[data-option-name]',
    ]
    for (const sel of candidates) {
      const el = fieldset.querySelector(sel)
      if (el) {
        const t = el.textContent.trim()
        if (t) return t
      }
    }
    return ''
  }

  function isColorGroup(fieldset) {
    const name = readOptionName(fieldset).toLowerCase()
    return name.startsWith('color') || name === 'colour'
  }

  // Find the scope element that both wraps this label AND contains the color
  // map JSON. Handles PDP (product-component / main) and spotlight ([data-product]).
  const SCOPE_SEL = '[data-product], product-component, main, body'

  function getColorMap(label) {
    const scope = label.closest(SCOPE_SEL) || document
    // Prefer scoped map (spotlight has multiple products), fall back to
    // document-wide (PDP where the map may be emitted next to the section).
    const el = scope.querySelector('script[data-bam-color-map]')
      || document.querySelector('script[data-bam-color-map]')
    if (!el) return {}
    try { return JSON.parse(el.textContent) } catch { return {} }
  }

  // Currently-visible product image element for this label:
  //   PDP (combined snippet)          → [data-main-image] inside [data-product]
  //   Spotlight (split media/info)    → [data-product-media][data-handle=<h>] [data-main-image] in the section
  //   Fallback (legacy Dawn gallery)  → .product__media-item img
  function findActiveImage(label) {
    const productWrap = label.closest('[data-product]')
    if (productWrap) {
      const inline = productWrap.querySelector('[data-main-image]')
      if (inline) return inline
      // Split layout — media lives in a sibling column keyed by handle.
      const handle = productWrap.dataset.handle
      const section = productWrap.closest('[data-spotlight], [data-spotlight-single]')
      if (handle && section) {
        const splitMedia = section.querySelector(`[data-product-media][data-handle="${handle}"] [data-main-image]`)
        if (splitMedia) return splitMedia
      }
    }
    const scope = label.closest(SCOPE_SEL) || document
    const anyMain = scope.querySelector('[data-main-image]')
    if (anyMain) return anyMain
    const active = scope.querySelector('.product__media-item.is-active img, .product__media-item img')
    return active || null
  }

  function wireHoverPreview(label, value) {
    if (label.dataset.bamHoverReady) return
    const slug = colorSlug(value)
    let cache = null
    label.addEventListener('mouseenter', () => {
      const map = getColorMap(label)
      const url = map[slug]
      if (!url) return
      const img = findActiveImage(label)
      if (!img) return
      cache = {
        img,
        src: img.getAttribute('src'),
        srcset: img.getAttribute('srcset'),
      }
      img.setAttribute('src', url)
      img.removeAttribute('srcset') // srcset would override our src
    })
    label.addEventListener('mouseleave', () => {
      if (!cache) return
      cache.img.setAttribute('src', cache.src ?? '')
      if (cache.srcset) cache.img.setAttribute('srcset', cache.srcset)
      cache = null
    })
    label.dataset.bamHoverReady = '1'
  }

  function readLabelValue(label) {
    // Dawn puts a "Variant sold out or unavailable" visually-hidden span
    // inside the label, so textContent is unreliable. The associated radio
    // input's `value` attr is the true option value.
    if (label.htmlFor) {
      const input = document.getElementById(label.htmlFor)
      if (input?.value) return input.value.trim()
    }
    const sibling = label.previousElementSibling
    if (sibling?.tagName === 'INPUT' && sibling.value) return sibling.value.trim()
    return (label.getAttribute('data-value') || label.dataset.value || label.textContent).trim()
  }

  function enhanceLabel(label) {
    const value = readLabelValue(label)
    if (!label.dataset.bamSwatchReady) {
      const swatch = makeSwatch(value, isLabelOOS(label))
      const textSpan = document.createElement('span')
      textSpan.className = 'bam-swatch-label'
      while (label.firstChild) textSpan.appendChild(label.firstChild)
      label.appendChild(swatch)
      label.appendChild(textSpan)
      label.setAttribute('data-bam-tooltip', value)
      label.dataset.bamSwatchReady = '1'
    }
    wireHoverPreview(label, value)
  }

  function enhanceFieldset(fs) {
    if (!isColorGroup(fs)) return
    fs.classList.add('bam-color-group')
    fs.querySelectorAll('label').forEach(enhanceLabel)
  }

  function enhance(root = document) {
    root.querySelectorAll('fieldset').forEach(enhanceFieldset)
  }

  function markReady() {
    const html = document.documentElement
    html.classList.remove('bam-pre-swatches')
    html.classList.add('bam-swatches-ready')
  }

  document.addEventListener('DOMContentLoaded', () => { enhance(); markReady() })
  document.addEventListener('shopify:section:load', (e) => { enhance(e.target); markReady() })

  // Watch for Dawn re-rendering the variant picker on option change.
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      // New fieldsets added anywhere
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return
        if (node.tagName === 'FIELDSET') enhanceFieldset(node)
        else if (node.querySelectorAll) node.querySelectorAll('fieldset').forEach(enhanceFieldset)
      })
      // Inner content of a fieldset changed (radios replaced but same fieldset element)
      const parentFs = m.target?.closest?.('fieldset')
      if (parentFs) enhanceFieldset(parentFs)
    }
  })
  observer.observe(document.body, { childList: true, subtree: true })

  // Belt & suspenders — Dawn sometimes replaces content between microtasks
  setTimeout(() => enhance(), 300)
  setTimeout(() => enhance(), 1200)
})()
